import { z } from "zod"

// API error response
export const ApiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.unknown()).optional(),
})

export type ApiError = z.infer<typeof ApiErrorSchema>;

// Message roles matching backend enum
export const MessageRoleSchema = z.enum([
  "user",
  "assistant",
  "system",
  "tool",
])

export type MessageRole = z.infer<typeof MessageRoleSchema>;

// Thread schemas
export const ThreadResponseSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  member_id: z.string().uuid().nullable().optional(),
  persona_id: z.number().nullable().optional(),
  title: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  data: z.record(z.unknown()).nullable().optional(),
  greeting: z.string().optional(),
})

export type ThreadResponse = z.infer<typeof ThreadResponseSchema>;

export const CreateThreadRequestSchema = z.object({
  title: z.string().max(500).optional(),
  data: z.record(z.unknown()).optional(),
})

export type CreateThreadRequest = z.infer<typeof CreateThreadRequestSchema>;

// Message schemas matching backend
export const MessageResponseSchema = z.object({
  id: z.number(),
  thread_id: z.string().uuid(),
  role: MessageRoleSchema,
  content: z.string().nullable().optional(),
  tool_calls: z.array(z.record(z.unknown())).nullable().optional(),
  tool_call_id: z.string().nullable().optional(),
  token_count: z.number().nullable().optional(),
  is_summary: z.boolean(),
  thinking_duration: z.number().nullable().optional(),
  created_at: z.string(),
  was_helpful: z.boolean().nullable().optional(),
})

export type MessageResponse = z.infer<typeof MessageResponseSchema>;

// Vote request schema
export const MessageVoteRequestSchema = z.object({
  was_helpful: z.boolean().nullable(),
})

export type MessageVoteRequest = z.infer<typeof MessageVoteRequestSchema>;

export const ThreadWithMessagesResponseSchema = z.object({
  thread: ThreadResponseSchema,
  messages: z.array(MessageResponseSchema),
})

export type ThreadWithMessagesResponse = z.infer<
  typeof ThreadWithMessagesResponseSchema
>;

export const PaginatedMessagesResponseSchema = z.object({
  messages: z.array(MessageResponseSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
})

export type PaginatedMessagesResponse = z.infer<
  typeof PaginatedMessagesResponseSchema
>;

// Request to send a message
export const SendMessageRequestSchema = z.object({
  content: z.string().min(1).max(100000),
})

export type SendMessageRequest = z.infer<typeof SendMessageRequestSchema>;

// Streaming event types matching backend
export const StreamEventTypeSchema = z.enum([
  "text",
  "tool_call",
  "complete",
  "trace",
  "error",
  "suggestions",
])

export type StreamEventType = z.infer<typeof StreamEventTypeSchema>;

// Tool call event body
export const ToolCallBodySchema = z.object({
  display: z.string(),
})

export type ToolCallBody = z.infer<typeof ToolCallBodySchema>;
// Display widget structure (used within complete event)
export const DisplayWidgetSchema = z.object({
  widget_type: z.string(),
  data: z.any(),
})

export type DisplayWidget = z.infer<typeof DisplayWidgetSchema>;

// Complete event body (final event with message_id and optional widgets)
export const CompleteEventBodySchema = z.object({
  message_id: z.number().optional(),
  widgets: z.array(DisplayWidgetSchema).optional(),
})

export type CompleteEventBody = z.infer<typeof CompleteEventBodySchema>;

// A single tappable follow-up prompt. `kind: "handoff"` routes to a care
// advocate and is styled distinctly; "answerable" is handled by a tool.
export const SuggestionChipSchema = z.object({
  text: z.string(),
  kind: z.enum(["answerable", "handoff"]).default("answerable"),
})

export type SuggestionChip = z.infer<typeof SuggestionChipSchema>;

// Suggestions event body (arrives after `complete`, keyed to its message)
export const SuggestionsEventBodySchema = z.object({
  message_id: z.number(),
  chips: z.array(SuggestionChipSchema),
})

export type SuggestionsEventBody = z.infer<typeof SuggestionsEventBodySchema>;

// Text event body - streaming content only (message_id now in complete event)
export const TextEventBodySchema = z.string()

export type TextEventBody = z.infer<typeof TextEventBodySchema>;

// Stream event schemas
export const StreamEventTextSchema = z.object({
  t: z.literal("text"),
  body: TextEventBodySchema,
})

export const TraceEventSchema = z.object({
  t: z.literal("trace"),
  body: z.record(z.unknown()),
})

export const StreamEventToolCallSchema = z.object({
  t: z.literal("tool_call"),
  body: ToolCallBodySchema,
})

export const StreamEventCompleteSchema = z.object({
  t: z.literal("complete"),
  body: CompleteEventBodySchema,
})

export const StreamEventErrorSchema = z.object({
  t: z.literal("error"),
  body: z.string(),
})

export const StreamEventSuggestionsSchema = z.object({
  t: z.literal("suggestions"),
  body: SuggestionsEventBodySchema,
})

export const StreamEventSchema = z.discriminatedUnion("t", [
  StreamEventTextSchema,
  StreamEventToolCallSchema,
  StreamEventCompleteSchema,
  StreamEventErrorSchema,
  StreamEventSuggestionsSchema,
  TraceEventSchema,
])

export type StreamEvent = z.infer<typeof StreamEventSchema>;
export type StreamEventText = z.infer<typeof StreamEventTextSchema>;
export type StreamEventToolCall = z.infer<typeof StreamEventToolCallSchema>;
export type StreamEventComplete = z.infer<typeof StreamEventCompleteSchema>;
export type StreamEventError = z.infer<typeof StreamEventErrorSchema>;
export type StreamEventSuggestions = z.infer<typeof StreamEventSuggestionsSchema>;
export type TraceEvent = z.infer<typeof TraceEventSchema>;
// Request to submit a tool result (widget response)
export const ToolResultRequestSchema = z.object({
  tool_call_id: z.string(),
  content: z.string(),
})

export type ToolResultRequest = z.infer<typeof ToolResultRequestSchema>;

// Runtime UI feature flags served by GET /config/ui (backend-driven, so they
// toggle per-environment without a frontend rebuild). Extra keys (e.g. dash-only
// flags) are ignored. All optional + defaulted so a partial/failed response
// fails closed (feature off).
export const UiConfigSchema = z.object({
  suggested_chips_enabled: z.boolean().default(false),
})

export type UiConfig = z.infer<typeof UiConfigSchema>;

// Rate limit info from response headers
export interface RateLimitInfo {
  limit: number;
  remaining: number;
  reset: number;
}

// API client configuration
export interface ApiClientConfig {
  baseUrl: string;
  getAuthToken: () => string | null;
}

export const TextResponseSchema = z.object({
  type: z.literal("text"),
  content: z.string(),
  conversationId: z.string(),
})

export const WidgetResponseSchema = z.object({
  type: z.literal("widget"),
  widgetType: z.string(),
  widgetData: z.record(z.unknown()),
  conversationId: z.string(),
})

export const ChatResponseSchema = z.discriminatedUnion("type", [
  TextResponseSchema,
  WidgetResponseSchema,
])

export type TextResponse = z.infer<typeof TextResponseSchema>;
export type WidgetResponse = z.infer<typeof WidgetResponseSchema>;
export type ChatResponse = z.infer<typeof ChatResponseSchema>;
